/**
 * Authentication Hook
 * Provides authentication state and utilities
 * 
 * ARCHITECTURAL STANDARD: Centralized auth logic
 * All authentication state management happens here
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { API_CONFIG } from '../config/api.config';
import apiClient from '../services/apiClient';

const hasUserInfo = (userInfo) => (
  userInfo &&
  typeof userInfo === 'object' &&
  !Array.isArray(userInfo) &&
  Boolean(userInfo.group) &&
  Boolean(userInfo.name) &&
  Boolean(userInfo.username)
);

const hasAuthenticatedUser = (userInfo) => (
  hasUserInfo(userInfo) &&
  Boolean(userInfo.role) &&
  Array.isArray(userInfo.permissions) &&
  userInfo.permissions.length > 0
);

/**
 * useAuth Hook
 * Manages authentication state and provides auth utilities
 * 
 * @returns {Object} Auth state and methods
 */
export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Check authentication status on mount
   */
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const storedUser = sessionStorage.getItem('user') || localStorage.getItem('mdm_user');
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          if (hasAuthenticatedUser(parsedUser)) {
            setUser(parsedUser);
            setIsAuthenticated(true);
          }
        }

        const response = await apiClient.get(API_CONFIG.auth.getUserInfo.endpoint);
        const userInfo = response?.data || {};
        if (!hasUserInfo(userInfo)) {
          throw new Error('User info not available');
        }

        const permissions = userInfo?.group === 'rw' ? ['user', 'admin'] : ['user'];
        const hydratedUser = {
          ...userInfo,
          role: permissions.includes('admin') ? 'admin' : 'user',
          permissions,
        };
        sessionStorage.setItem('user', JSON.stringify(hydratedUser));
        setUser(hydratedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Auth check failed:', error);
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('user');
        localStorage.removeItem('mdm_auth_token');
        localStorage.removeItem('mdm_user');
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  /**
   * Login user
   * TODO: Connect to actual login API in T-0004
   * 
   * @param {string} username - User email/username
   * @param {string} password - User password
   * @returns {Promise<boolean>} Login success status
   */
  const login = useCallback(async (username, password) => {
    try {
      setIsLoading(true);
      await apiClient.post(API_CONFIG.auth.login.endpoint, { username, password });
      const response = await apiClient.get(API_CONFIG.auth.getUserInfo.endpoint);

      const userInfo = response?.data || {};
      if (!hasUserInfo(userInfo)) {
        throw new Error('User info not available');
      }

      const permissions = userInfo?.group === 'rw' ? ['user', 'admin'] : ['user'];
      const hydratedUser = {
        ...userInfo,
        role: permissions.includes('admin') ? 'admin' : 'user',
        permissions,
      };

      sessionStorage.setItem('authToken', 'session');
      sessionStorage.setItem('user', JSON.stringify(hydratedUser));
      setUser(hydratedUser);
      setIsAuthenticated(true);

      // toast.success('Login successful');
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      toast.error('Login failed: ' + (error.message || 'Unknown error'));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('user');
    localStorage.removeItem('mdm_auth_token');
    localStorage.removeItem('mdm_user');
    try {
      await apiClient.post(API_CONFIG.auth.logout.endpoint);
      setUser(null);
      setIsAuthenticated(false);
      // toast.info('Logged out successfully');
    } catch (error) {
      console.error('Logout failed:', error);
      // toast.error('Logout failed');
    }
  }, []);

  /**
   * Check if user has specific permission
   * 
   * @param {string} permission - Permission to check
   * @returns {boolean} Has permission
   */
  const hasPermission = useCallback(
    (permission) => {
      if (!user || !user.permissions) {
        return false;
      }
      return user.permissions.includes(permission);
    },
    [user]
  );

  /**
   * Check if user has any of the specified permissions
   * 
   * @param {Array<string>} permissions - Permissions to check
   * @returns {boolean} Has any permission
   */
  const hasAnyPermission = useCallback(
    (permissions) => {
      if (!user || !user.permissions || !permissions) {
        return false;
      }
      return permissions.some((permission) => user.permissions.includes(permission));
    },
    [user]
  );

  /**
   * Refresh auth token
   * TODO: Implement token refresh logic
   */
  const refreshToken = useCallback(async () => {
    // TODO: Call refresh token API
    // const response = await apiClient.post('/auth/refresh');
    // localStorage.setItem('mdm_auth_token', response.data.token);
    return true;
  }, []);

  const authValue = useMemo(
    () => ({
      isAuthenticated,
      user,
      isLoading,
      login,
      logout,
      hasPermission,
      hasAnyPermission,
      refreshToken,
    }),
    [isAuthenticated, user, isLoading, login, logout, hasPermission, hasAnyPermission, refreshToken]
  );

  return authValue;
};

export default useAuth;
